import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import AdminSyncAPI from '../../api/admin/sync.js';
import { useNavigate } from 'react-router-dom';
import './AdminSync.css';

/**
 * Admin Sync Dashboard
 * Manage tenant synchronization with Pabau
 * 
 * Created: August 2025
 * Purpose: Monitor and control tenant data synchronization processes
 */

// Helper function to create entity groups from API response
const createEntityGroups = (entities) => {
  // Entity categorization based on business logic
  const groups = {
    'Foundation': [],
    'Categories': [],
    'Forms': [],
    'Products & Services': [],
    'Business Partners': [],
    'Financial': [],
    'Operations': [],
    'Sales & Marketing': [],
    'Management': []
  };

  // Categorize entities based on name patterns and business logic
  Object.keys(entities).forEach(entityName => {
    const entity = entities[entityName];
    const phase = entity.phase;
    
    // Categorize by name patterns and phase
    if (['clients', 'drugs', 'locations', 'services', 'users'].includes(entityName)) {
      groups['Foundation'].push(entityName);
    } else if (entityName.includes('_categories') || entityName.includes('category')) {
      groups['Categories'].push(entityName);
    } else if (entityName.includes('form') || entityName.includes('client_form')) {
      groups['Forms'].push(entityName);
    } else if (['packages', 'products', 'quotes', 'quote_template'].includes(entityName)) {
      groups['Products & Services'].push(entityName);
    } else if (['insurers', 'suppliers'].includes(entityName)) {
      groups['Business Partners'].push(entityName);
    } else if (['discounts', 'invoices', 'payment_types', 'payments', 'tax_distributions', 'taxes'].includes(entityName)) {
      groups['Financial'].push(entityName);
    } else if (['activities', 'appointments', 'schedules'].includes(entityName)) {
      groups['Operations'].push(entityName);
    } else if (['leads', 'pipelines'].includes(entityName)) {
      groups['Sales & Marketing'].push(entityName);
    } else {
      // Everything else goes to Management
      groups['Management'].push(entityName);
    }
  });

  // Convert to array format and filter out empty groups
  return Object.entries(groups)
    .filter(([group, entities]) => entities.length > 0)
    .map(([group, entities]) => ({ group, entities }));
};

// Returns the clinic UUID we can safely hand to the admin sync API. The
// `/workflows/start/{tenant_id}` endpoint validates against `saas_core.clinic.id`
// (UUID). `entry.tenant_id` on a history row is the raw VARCHAR — often a schema
// name when the JOIN missed — so only fall back to it when it's UUID-shaped.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const resolveTenantUuid = (entry) => {
  if (!entry) return null;
  if (entry.clinic_id) return entry.clinic_id;
  if (entry.tenant_id && UUID_RE.test(entry.tenant_id)) return entry.tenant_id;
  return null;
};

// `/admin/sync/workflows/history` pre-formats only `full`, `phase_5`, and
// `phase_*`; everything else (`full_sync`, `incremental_sync`, `client_fanout`,
// `entity`, …) arrives as the raw machine label. Render those nicely; leave
// already-formatted backend strings alone by falling through to the default.
const formatWorkflowType = (entry) => {
  const raw = entry?.workflow_type;
  if (!raw) return '-';
  switch (raw) {
    case 'full_sync':
      return 'Full Sync';
    case 'incremental_sync':
      return 'Incremental Sync';
    case 'client_fanout':
      return 'Client Fan-out';
    case 'entity':
      return `Entity: ${entry.entity_name || 'Multiple'}`;
    default:
      return raw;
  }
};

const AdminSync = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [syncStatus, setSyncStatus] = useState({});
  const [syncHistory, setSyncHistory] = useState([]);
  const [workflowStatus, setWorkflowStatus] = useState({});
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Hierarchical monitoring state
  const [expandedWorkflows, setExpandedWorkflows] = useState(new Set());
  const [workflowEntities, setWorkflowEntities] = useState({});
  const [loadingEntities, setLoadingEntities] = useState({});
  
  // Sync control state
  const [selectedSyncTenant, setSelectedSyncTenant] = useState('all');
  const [syncMode, setSyncMode] = useState('full'); // full, incremental, entity, client_fanout
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [withoutDependencies, setWithoutDependencies] = useState(false);

  // Client Fan-out state
  const [clientFanoutScope, setClientFanoutScope] = useState('all');
  const [alphaBucket, setAlphaBucket] = useState('A-C');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Available entities (loaded dynamically from API)
  const [availableEntities, setAvailableEntities] = useState([]);

  // Load sync data on mount
  useEffect(() => {
    // Debug: Check admin token
    const adminToken = localStorage.getItem('admin_token');
    console.log('AdminSync component - admin token check:', adminToken ? 'Token exists' : 'No token found');
    if (!adminToken) {
      // Redirect to admin login if token missing
      navigate('/admin/login');
      return;
    }

    loadSyncData();
  }, [navigate]);

  // Auto-refresh every 20 seconds if enabled (reduced polling to lower DB load)
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      loadSyncData(true); // Silent refresh
    }, 20000);
    
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadSyncData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      // Staggered requests to reduce backend concurrency spikes
      // Small jitter to avoid request burst
      const sleep = (ms) => new Promise(res => setTimeout(res, ms));
      const jitter = () => Math.floor(50 + Math.random() * 200);

      const tenantsData = await AdminSyncAPI.getTenantsWithSyncConfig();
      await sleep(jitter());
      const statusData = await AdminSyncAPI.getSyncStatus();
      await sleep(jitter());
      const historyData = await AdminSyncAPI.getSyncHistory({ limit: 20 });
      await sleep(jitter());
      const workflowStatusData = await AdminSyncAPI.getWorkflowStatus();
      await sleep(jitter());
      const workflowHistoryData = await AdminSyncAPI.getWorkflowHistory({ limit: 50, hours: 168 });
      await sleep(jitter());
      const entitiesData = await AdminSyncAPI.getAvailableEntities();

      setTenants(tenantsData);
      setSyncStatus(statusData);
      setSyncHistory(historyData);
      setWorkflowStatus(workflowStatusData);
      // workflowHistoryData is an object with 'history' array property
      setWorkflowHistory(workflowHistoryData?.history || []);

      // Load available entities and create groups
      const groupedEntities = createEntityGroups(entitiesData.entities);
      setAvailableEntities(groupedEntities);

      // Check for any syncs in progress (combine legacy and workflow status)
      const inProgress = {};
      Object.entries(statusData).forEach(([tenantId, status]) => {
        if (status.state === 'running' || status.state === 'pending') {
          inProgress[tenantId] = true;
        }
      });
      
      // Also check workflow status
      if (workflowStatusData.running_workflows) {
        workflowStatusData.running_workflows.forEach(workflow => {
          if (workflow.status === 'running' || workflow.status === 'pending') {
            inProgress[workflow.tenant_id] = true;
          }
        });
      }
      
      setSyncInProgress(inProgress);

    } catch (err) {
      console.error('Failed to load sync data:', err);
      if (!silent) setError('Failed to load sync data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const startSync = async (tenantId, syncType = 'full', entityName = null) => {
    try {
      setSyncInProgress({ ...syncInProgress, [tenantId]: true });

      // Use new workflow orchestration system
      const result = await AdminSyncAPI.startWorkflow(tenantId, {
        workflow_type: entityName ? 'entity' : 'full',
        entity_name: entityName,
        with_dependencies: true
      });

      console.log('Workflow started:', result);

      // Refresh data after starting sync
      await loadSyncData();

    } catch (err) {
      console.error('Failed to start workflow:', err);
      setError(`Failed to start sync: ${err.message}`);
    } finally {
      // Always clear the per-tenant in-flight flag — success or error.
      // Without this, the local syncInProgress map stays at true and
      // disables the sync button permanently (see #238). Server-side
      // "is this tenant syncing" visibility is sourced from
      // `perTenant?.status === 'running'` at the row level.
      setSyncInProgress(prevState => ({ ...prevState, [tenantId]: false }));
    }
  };

  const stopSync = async (tenantId) => {
    try {
      await AdminSyncAPI.stopSync(tenantId);
      
      // Refresh data after stopping sync
      await loadSyncData();
      
    } catch (err) {
      console.error('Failed to stop sync:', err);
      setError(`Failed to stop sync: ${err.message}`);
    }
  };

  const configureTenantSync = async (tenantId, config) => {
    try {
      await AdminSyncAPI.updateSyncConfig(tenantId, config);
      
      // Refresh data after updating config
      await loadSyncData();
      
    } catch (err) {
      console.error('Failed to update sync config:', err);
      setError(`Failed to update sync configuration: ${err.message}`);
    }
  };

  const retryFailedSync = async (tenantId, syncJobId) => {
    try {
      await AdminSyncAPI.retrySync(tenantId, syncJobId);
      
      // Refresh data after retry
      await loadSyncData();
      
    } catch (err) {
      console.error('Failed to retry sync:', err);
      setError(`Failed to retry sync: ${err.message}`);
    }
  };

  const executeSync = async () => {
    try {
      setError(null);

      // Validate entity selection for entity mode
      if (syncMode === 'entity' && selectedEntity === 'all') {
        setError('❌ Please select a specific entity for entity sync');
        return;
      }

      // Validate client_fanout mode
      if (syncMode === 'client_fanout') {
        // Client Fan-out is single-tenant only
        if (selectedSyncTenant === 'all') {
          setError('❌ Client Fan-out requires a single tenant selection');
          return;
        }

        // Validate search scope has selections
        if (clientFanoutScope === 'search' && selectedClients.length === 0) {
          setError('❌ Please search and select at least one client for search scope');
          return;
        }
      }

      // Determine which tenants to sync
      const tenantsToSync = selectedSyncTenant === 'all'
        ? (tenants || []).map(t => t.id)
        : [selectedSyncTenant];

      // Execute workflow-based sync with small per-tenant jitter
      const sleep = (ms) => new Promise(res => setTimeout(res, ms));
      const syncPromises = tenantsToSync.map(async (tenantId, idx) => {
        // jitter 0-300ms plus 100ms per index to spread when syncing many tenants
        await sleep((Math.random() * 300) + (idx * 100));

        if (syncMode === 'full') {
          // Full Sync - all entities with full data fetch
          return AdminSyncAPI.startWorkflow(tenantId, {
            workflow_type: 'full_sync'
          });
        } else if (syncMode === 'incremental') {
          // Incremental Sync - all entities with incremental data fetch
          return AdminSyncAPI.startWorkflow(tenantId, {
            workflow_type: 'incremental_sync'
          });
        } else if (syncMode === 'client_fanout') {
          // Client Fan-out - backfill all client sub-entities
          return AdminSyncAPI.startWorkflow(tenantId, {
            workflow_type: 'client_fanout',
            client_scope: clientFanoutScope,
            search_query: clientFanoutScope === 'search' ? searchQuery : null,
            alpha_bucket: clientFanoutScope === 'alpha_slice' ? alphaBucket : null,
            client_ids: selectedClients.length > 0 ? selectedClients.map(c => c.id) : null
          });
        } else if (syncMode === 'entity') {
          // Entity Sync - single entity
          return AdminSyncAPI.startWorkflow(tenantId, {
            workflow_type: 'entity',
            entity_name: selectedEntity,
            with_dependencies: !withoutDependencies
          });
        }
      });

      // Mark tenants as syncing
      const newSyncProgress = { ...syncInProgress };
      tenantsToSync.forEach(tenantId => {
        newSyncProgress[tenantId] = true;
      });
      setSyncInProgress(newSyncProgress);

      await Promise.all(syncPromises);

      // Refresh data
      await loadSyncData();

      // Clear the local in-flight flag for the tenants we just triggered.
      // Without this, the sync button's `disabled={Object.values(syncInProgress).some(Boolean)}`
      // check at the bottom of this component stays true forever after the
      // first successful trigger, blocking any further sync (see #238).
      // Downstream "is this tenant currently syncing" visibility is sourced
      // from the server-side per-tenant status (`perTenant?.status` in the
      // tenant-row rendering), not from this local UI flag.
      setSyncInProgress(prevState => {
        const newState = { ...prevState };
        tenantsToSync.forEach(tenantId => {
          newState[tenantId] = false;
        });
        return newState;
      });

      // Show success message
      const tenantNames = tenantsToSync.length === (tenants || []).length
        ? 'all tenants'
        : tenantsToSync.map(id => {
            const tenant = (tenants || []).find(t => t.id === id);
            return tenant ? tenant.name : id;
          }).join(', ');

      let modeLabel = syncMode;
      if (syncMode === 'entity') {
        modeLabel = `${selectedEntity} entity sync`;
      } else if (syncMode === 'client_fanout') {
        const scopeLabel = clientFanoutScope === 'all' ? 'all clients' :
                          clientFanoutScope === 'search' ? `${selectedClients.length} selected clients` :
                          `alphabet ${alphaBucket}`;
        modeLabel = `Client Fan-out (${scopeLabel})`;
      } else {
        modeLabel = `${syncMode} sync`;
      }
      setError(`✅ ${modeLabel} started successfully for ${tenantNames}`);
      setTimeout(() => setError(null), 5000);

    } catch (err) {
      console.error('Failed to execute sync:', err);
      setError(`❌ Failed to start sync: ${err.message}`);

      // Reset sync progress for failed tenants
      setSyncInProgress(prevState => {
        const newState = { ...prevState };
        Object.keys(newState).forEach(tenantId => {
          newState[tenantId] = false;
        });
        return newState;
      });
    }
  };

  // Client search with debouncing using useEffect
  React.useEffect(() => {
    // Clear results if query is too short
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    // Only search if we have a tenant selected and we're in search mode
    if (!selectedSyncTenant || selectedSyncTenant === 'all' || clientFanoutScope !== 'search') {
      return;
    }

    setSearchLoading(true);

    // Debounce: wait 300ms before searching
    const timeoutId = setTimeout(async () => {
      try {
        const results = await AdminSyncAPI.searchClients(selectedSyncTenant, searchQuery);
        setSearchResults(results.results || []);
      } catch (err) {
        console.error('Failed to search clients:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    // Cleanup function: cancel pending search if query changes
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchQuery, selectedSyncTenant, clientFanoutScope]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const toggleClientSelection = (client) => {
    const isSelected = selectedClients.some(c => c.id === client.id);
    if (isSelected) {
      setSelectedClients(selectedClients.filter(c => c.id !== client.id));
    } else {
      setSelectedClients([...selectedClients, client]);
    }
  };

  const removeClient = (clientId) => {
    setSelectedClients(selectedClients.filter(c => c.id !== clientId));
  };

  const getSyncStateClass = (state) => {
    switch (state) {
      case 'running': return 'sync-running';
      case 'completed': return 'sync-completed';
      case 'failed': return 'sync-failed';
      case 'pending': return 'sync-pending';
      default: return 'sync-idle';
    }
  };

  const toggleWorkflowExpansion = async (workflowId) => {
    const newExpanded = new Set(expandedWorkflows);
    
    if (newExpanded.has(workflowId)) {
      // Collapse - remove from expanded set
      newExpanded.delete(workflowId);
      setExpandedWorkflows(newExpanded);
    } else {
      // Expand - add to expanded set and load entity details if not loaded
      newExpanded.add(workflowId);
      setExpandedWorkflows(newExpanded);
      
      if (!workflowEntities[workflowId]) {
        await loadWorkflowEntities(workflowId);
      }
    }
  };

  const loadWorkflowEntities = async (workflowId) => {
    try {
      setLoadingEntities(prev => ({ ...prev, [workflowId]: true }));
      const entities = await AdminSyncAPI.getWorkflowEntities(workflowId);
      console.log('✅ API Response received:', entities);
      console.log('✅ Response structure:', {
        hasEntities: !!entities?.entities,
        entitiesLength: entities?.entities?.length,
        hasSummary: !!entities?.summary,
        summaryKeys: entities?.summary ? Object.keys(entities.summary) : null
      });
      setWorkflowEntities(prev => ({ ...prev, [workflowId]: entities }));
    } catch (err) {
      console.error('Failed to load workflow entities:', err);
    } finally {
      setLoadingEntities(prev => ({ ...prev, [workflowId]: false }));
    }
  };

  const getSubTaskIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'running': return '🔄';
      case 'failed': return '❌';
      case 'pending': return '⏳';
      default: return '⏳';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-sync-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading sync dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-sync">
        <div className="sync-header">
          <h1>Tenant Sync Management</h1>
          <div className="sync-controls">
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (20s)
            </label>
            <button onClick={() => loadSyncData()} className="refresh-button">
              🔄 Refresh Now
            </button>
          </div>
        </div>

        {/* Sync Control Panel */}
        <div className="sync-control-panel">
          <h2>🚀 Execute Sync</h2>
          <div className="control-grid">

            {/* Selector 1: Choose Tenant */}
            <div className="control-group">
              <label>Target Tenant(s)</label>
              <select
                value={selectedSyncTenant}
                onChange={(e) => {
                  setSelectedSyncTenant(e.target.value);
                  // Clear client fanout search state when tenant changes to prevent stale client IDs
                  setSearchQuery('');
                  setSearchResults([]);
                  setSelectedClients([]);
                }}
                className="control-select"
              >
                <option value="all" disabled={syncMode === 'client_fanout'}>
                  {syncMode === 'client_fanout' ? '🌐 All Tenants (single-tenant only for Fan-out)' : '🌐 All Tenants'}
                </option>
                {(tenants || []).map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} {tenant.sync_enabled ? '✅' : '⚠️'}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector 2: Sync Mode (Consolidated) */}
            <div className="control-group">
              <label>Sync Mode</label>
              <select
                value={syncMode}
                onChange={(e) => {
                  setSyncMode(e.target.value);
                  // Reset client fanout state when changing modes
                  if (e.target.value !== 'client_fanout') {
                    setClientFanoutScope('all');
                    setSearchQuery('');
                    setSearchResults([]);
                    setSelectedClients([]);
                  }
                  // Enforce single-tenant for client_fanout
                  if (e.target.value === 'client_fanout' && selectedSyncTenant === 'all') {
                    // Auto-select first tenant if available
                    if (tenants && tenants.length > 0) {
                      setSelectedSyncTenant(tenants[0].id);
                    }
                  }
                }}
                className="control-select"
              >
                <option value="full">🔄 Full Sync</option>
                <option value="incremental">⚡ Incremental Sync</option>
                <option value="entity">📊 Entity Sync</option>
                <option value="client_fanout">📋 Client Fan-out</option>
              </select>
            </div>

            {/* Client Fan-out Scope Controls */}
            {syncMode === 'client_fanout' && (
              <div className="control-group">
                <label>Client Scope</label>
                <select
                  value={clientFanoutScope}
                  onChange={(e) => {
                    setClientFanoutScope(e.target.value);
                    // Reset scope-specific state
                    setSearchQuery('');
                    setSearchResults([]);
                    setSelectedClients([]);
                  }}
                  className="control-select"
                >
                  <option value="all">🌐 All Clients</option>
                  <option value="search">🔍 Search Clients</option>
                  <option value="alpha_slice">🔤 Alphabet Slice</option>
                </select>

                {/* Search scope UI */}
                {clientFanoutScope === 'search' && (
                  <div style={{ marginTop: '10px' }}>
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="control-select"
                      style={{ marginBottom: '8px' }}
                    />
                    {searchLoading && <div style={{ fontSize: '12px', color: '#666' }}>Searching...</div>}

                    {/* Selected clients chips */}
                    {selectedClients.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                          Selected ({selectedClients.length}):
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {selectedClients.map(client => (
                            <span
                              key={client.id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}
                            >
                              {client.display}
                              <button
                                onClick={() => removeClient(client.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '0 2px',
                                  color: '#666'
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Search results */}
                    {searchResults.length > 0 && (
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}>
                        {searchResults.map(client => (
                          <div
                            key={client.id}
                            onClick={() => toggleClientSelection(client)}
                            style={{
                              padding: '8px',
                              cursor: 'pointer',
                              backgroundColor: selectedClients.some(c => c.id === client.id) ? '#e3f2fd' : 'white',
                              borderBottom: '1px solid #eee'
                            }}
                          >
                            {client.display}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Alphabet slice scope UI */}
                {clientFanoutScope === 'alpha_slice' && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                      Alphabet Bucket (by surname):
                    </label>
                    <select
                      value={alphaBucket}
                      onChange={(e) => setAlphaBucket(e.target.value)}
                      className="control-select"
                    >
                      <option value="A-C">A–C</option>
                      <option value="D-F">D–F</option>
                      <option value="G-I">G–I</option>
                      <option value="J-L">J–L</option>
                      <option value="M-R">M–R</option>
                      <option value="S-Z">S–Z</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Selector 3: Choose specific Entity (only shown when entity mode selected) */}
            {syncMode === 'entity' && (
              <div className="control-group">
                <label>Select Entity</label>
                <select
                  value={selectedEntity}
                  onChange={(e) => setSelectedEntity(e.target.value)}
                  className="control-select"
                >
                  <option value="all">📋 Select an entity</option>
                  {availableEntities.map(group => (
                    <optgroup key={group.group} label={`— ${group.group} —`}>
                      {group.entities.map(entity => (
                        <option key={entity} value={entity}>
                          {entity.charAt(0).toUpperCase() + entity.slice(1).replace(/_/g, ' ')}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {/* Custom Options */}
            <div className="control-group options-group">
              <label>Options</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={withoutDependencies}
                    onChange={(e) => setWithoutDependencies(e.target.checked)}
                  />
                  🚫 Without Dependencies
                </label>
              </div>
            </div>

            {/* Execute Button */}
            <div className="control-group execute-group">
              <button 
                onClick={executeSync}
                className="execute-sync-btn"
                disabled={Object.values(syncInProgress).some(Boolean)}
              >
                {Object.values(syncInProgress).some(Boolean) ? (
                  <>🔄 Syncing...</>
                ) : (
                  <>🚀 Execute Sync</>
                )}
              </button>
              
              {selectedSyncTenant !== 'all' && (
                <div className="sync-target-info">
                  Target: {selectedSyncTenant === 'all' ? 'All tenants' : 
                    (tenants || []).find(t => t.id === selectedSyncTenant)?.name || selectedSyncTenant}
                </div>
              )}
            </div>

          </div>
        </div>

        {error && (
          <div className="sync-error">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
            <button onClick={() => setError(null)} className="close-error">×</button>
          </div>
        )}

        {/* Enhanced Sync Overview Stats */}
        <div className="sync-stats">
          <div className="stat-card">
            <h3>Active Workflows</h3>
            <div className="stat-value">
              {workflowStatus.running_workflows ? workflowStatus.running_workflows.filter(w => 
                w.status === 'running' || w.status === 'pending'
              ).length : Object.values(syncInProgress).filter(Boolean).length}
            </div>
          </div>
          <div className="stat-card">
            <h3>Configured Tenants</h3>
            <div className="stat-value">
              {(tenants || []).filter(t => t.sync_enabled).length} / {(tenants || []).length}
            </div>
          </div>
          <div className="stat-card">
            <h3>Last 24h Syncs</h3>
            <div className="stat-value">
              {(workflowHistory || []).filter(h => {
                const time = new Date(h.started_at || h.created_at);
                return time > new Date(Date.now() - 24 * 60 * 60 * 1000);
              }).length}
            </div>
          </div>
          <div className="stat-card">
            <h3>Failed Syncs (7d)</h3>
            <div className="stat-value error">
              {(workflowHistory || []).filter(h => h.status === 'failed').length}
            </div>
          </div>
        </div>

        {/* Real-Time Workflow Status */}
        {workflowStatus.running_workflows && workflowStatus.running_workflows.length > 0 && (
          <div className="workflow-status-section">
            <h2>🔄 Active Sync Workflows</h2>
            <div className="workflow-status-grid">
              {workflowStatus.running_workflows.map(workflow => {
                const tenant = tenants.find(t => t.id === workflow.tenant_id);
                const totalEntities = workflow.total_entities || 0;
                const completedEntities = workflow.completed_entities || 0;
                const failedEntities = workflow.failed_entities || 0;
                const progressPercent = workflow.progress_percent != null
                  ? workflow.progress_percent
                  : (totalEntities > 0 ? (completedEntities / totalEntities) * 100 : 0);

                return (
                  <div key={workflow.workflow_id} className={`workflow-card ${workflow.status}`}>
                    <div className="workflow-header">
                      <h3>{tenant?.name || workflow.tenant_name || workflow.tenant_id}</h3>
                      <span className={`workflow-status ${workflow.status}`}>
                        {workflow.status}
                      </span>
                    </div>

                    <div className="workflow-progress">
                      <div className="progress-summary">
                        <span className="progress-text">
                          {completedEntities}/{totalEntities} entities completed
                          {failedEntities > 0 && ` (${failedEntities} failed)`}
                        </span>
                        {workflow.started_at && (
                          <span className="workflow-time">
                            Started: {new Date(workflow.started_at).toLocaleString()}
                          </span>
                        )}
                      </div>

                      {totalEntities > 0 && (
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Enhanced Tenant Sync Table with Workflow Status */}
        <div className="sync-table-section">
          <h2>🏢 Tenant Synchronization Status</h2>
          <div className="sync-table">
            <table>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Sync Enabled</th>
                  <th>Current Status</th>
                  <th>Last Sync</th>
                  <th>Progress</th>
                  <th>Records Synced</th>
                  <th>Configure</th>
                </tr>
              </thead>
              <tbody>
                {(tenants || []).map(tenant => {
                  // Use tenant_status keyed by tenant.id (recommended by senior dev)
                  const perTenant = (workflowStatus && workflowStatus.tenant_status)
                    ? workflowStatus.tenant_status[tenant.id]
                    : null;

                  const currentWorkflow = perTenant?.current_workflow || null;
                  const lastWorkflow = perTenant?.last_sync || null;

                  // Backwards-compatible fallbacks
                  const status = syncStatus[tenant.id] || {};
                  const isRunning = syncInProgress[tenant.id] || (perTenant?.status === 'running');

                  // Use aggregate entity counts
                  const totalEntities = currentWorkflow?.total_entities || 0;
                  const completedEntities = currentWorkflow?.completed_entities || 0;
                  const failedEntities = currentWorkflow?.failed_entities || 0;
                  
                  return (
                    <tr key={tenant.id} className={isRunning ? 'tenant-running' : ''}>
                      <td className="tenant-name">
                        <strong>{tenant.name}</strong>
                        <br />
                        <small>{tenant.clinic_id}</small>
                      </td>
                      <td>
                        <label className="sync-toggle">
                          <input 
                            type="checkbox" 
                            checked={tenant.sync_enabled || false}
                            onChange={(e) => configureTenantSync(tenant.id, {
                              sync_enabled: e.target.checked
                            })}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </td>
                      <td className="status-cell">
                        {currentWorkflow ? (
                          <>
                            <span className={`sync-status ${getSyncStateClass('running')}`}>
                              running
                            </span>
                            <div className="workflow-type">
                              {currentWorkflow.type || 'Full Workflow'}
                            </div>
                            {currentWorkflow.started_at && (
                              <div className="sync-start-time">
                                Started: {new Date(currentWorkflow.started_at).toLocaleTimeString()}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <span className={`sync-status ${getSyncStateClass(status.state)}`}>
                              {status.state || (perTenant?.status ?? 'idle')}
                            </span>
                            {status.error && (
                              <div className="sync-error-detail">
                                {status.error}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="last-sync-cell">
                        {lastWorkflow?.completed_at || status.last_sync_at ? (
                          <>
                            <div>{new Date((lastWorkflow?.completed_at || status.last_sync_at)).toLocaleString()}</div>
                            {lastWorkflow?.entities_synced != null && (
                              <small>{lastWorkflow.entities_synced} entities</small>
                            )}
                          </>
                        ) : (
                          <span className="no-sync">Never synced</span>
                        )}
                      </td>
                      <td className="progress-cell">
                        {currentWorkflow && totalEntities > 0 ? (
                          <>
                            <div className="progress-info">
                              <div className="progress-numbers">
                                {completedEntities}/{totalEntities} entities
                              </div>
                              <div className="mini-progress-bar">
                                <div
                                  className="mini-progress-fill"
                                  style={{ width: `${(completedEntities / totalEntities) * 100}%` }}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <span className="no-progress">-</span>
                        )}
                      </td>
                      <td className="records-cell">
                        {currentWorkflow && totalEntities > 0 ? (
                          <>
                            <div className="entity-summary">
                              <strong>{completedEntities}/{totalEntities}</strong> entities
                            </div>
                            {failedEntities > 0 && (
                              <small className="failed-summary">
                                {failedEntities} failed
                              </small>
                            )}
                          </>
                        ) : (lastWorkflow?.entities_synced !== null && lastWorkflow?.entities_synced !== undefined) ? (
                          <>
                            <strong>{lastWorkflow.entities_synced}</strong> entities
                            <br />
                            <small>Last sync</small>
                          </>
                        ) : (
                          <span className="no-data">-</span>
                        )}
                      </td>
                      <td className="config-cell" style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => setSelectedTenant(tenant)}
                          className="config-button"
                          title="Configure sync settings"
                        >
                          ⚙️ Configure
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Enhanced Sync History with Workflow Details */}
        <div className="sync-history-section">
          <h2>📊 Recent Sync History</h2>
          <div className="sync-history">
            {(workflowHistory || []).length === 0 && (syncHistory || []).length === 0 ? (
              <div className="no-history">No sync history available</div>
            ) : (
              <div className="history-table">
                <table>
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Duration</th>
                      <th>Progress</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Show workflow history first (newest) */}
                    {(workflowHistory || []).map(entry => {
                      // Match by tenant name instead of ID (Fix #5 - Critical data fix)
                      const tenant = tenants.find(t => t.name === entry.tenant_name);

                      // Use aggregate fields from entry instead of entity_progress
                      const totalEntities = entry.entities_total || 0;
                      const completedEntities = entry.entities_completed || 0;
                      const failedEntities = entry.entities_failed || 0;
                      const duration = entry.completed_at && entry.started_at ?
                        Math.floor((new Date(entry.completed_at) - new Date(entry.started_at)) / 1000) : null;
                      
                      const workflowId = entry.workflow_id || entry.id; // Handle both field names
                      const isExpanded = expandedWorkflows.has(workflowId);
                      const entityData = workflowEntities[workflowId];
                      const isLoadingEntities = loadingEntities[workflowId];

                      return (
                        <React.Fragment key={`workflow-${workflowId}`}>
                          <tr className={`history-row ${entry.status} ${isExpanded ? 'expanded' : ''}`}>
                            <td className="tenant-cell">
                              <div className="tenant-info-with-expand">
                                <button
                                  className="expand-button"
                                  onClick={() => toggleWorkflowExpansion(workflowId)}
                                  title={isExpanded ? 'Collapse entity details' : 'Expand entity details'}
                                >
                                  {isExpanded ? '🔽' : '▶️'}
                                </button>
                                <div className="tenant-details">
                                  <strong>{entry.tenant_name || 'Unknown Tenant'}</strong>
                                </div>
                              </div>
                            </td>
                            <td className="status-cell">
                              <span className={`status-badge ${entry.status}`}>
                                {entry.status === 'completed' ? 'COMPLETED' :
                                 entry.status === 'running' ? 'RUNNING' :
                                 entry.status === 'failed' ? 'FAILED' : entry.status?.toUpperCase()}
                              </span>
                              {failedEntities > 0 && (
                                <div className="failed-count-indicator">
                                  ❌ {failedEntities} failed
                                </div>
                              )}
                            </td>
                            <td className="type-cell">
                              <span className="sync-type">{formatWorkflowType(entry)}</span>
                            </td>
                            <td className="duration-cell">
                              {entry.status === 'running' || entry.status === 'pending' ? (
                                <div className="running-duration">
                                  <div className="duration-label">⏱️ Running</div>
                                  {entry.started_at && (
                                    <small className="started-time">
                                      Started: {new Date(entry.started_at).toLocaleTimeString()}
                                    </small>
                                  )}
                                </div>
                              ) : (
                                <div className="completed-duration">
                                  <div className="duration-value">
                                    {duration ? formatDuration(duration) : '-'}
                                  </div>
                                  {entry.completed_at && (
                                    <small className="completed-time">
                                      {new Date(entry.completed_at).toLocaleString()}
                                    </small>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="progress-cell">
                              {totalEntities > 0 ? (
                                <div className="progress-info">
                                  <div className="progress-numbers">
                                    {completedEntities}/{totalEntities} entities
                                  </div>
                                  <div className="mini-progress-bar">
                                    <div
                                      className="mini-progress-fill"
                                      style={{ width: `${(completedEntities / totalEntities) * 100}%` }}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <span className="no-progress">-</span>
                              )}
                            </td>
                            <td className="actions-cell">
                              {(() => {
                                const resolvedTenantUuid = resolveTenantUuid(entry);
                                const noTenantTitle = 'No clinic UUID on this history row — cannot retry/stop';
                                if (entry.status === 'failed') {
                                  return (
                                    <button
                                      onClick={() => resolvedTenantUuid && startSync(resolvedTenantUuid, 'full')}
                                      className="retry-button"
                                      title={resolvedTenantUuid ? 'Retry this workflow' : noTenantTitle}
                                      disabled={!resolvedTenantUuid}
                                    >
                                      🔄 Retry
                                    </button>
                                  );
                                }
                                if (entry.status === 'running' || entry.status === 'pending') {
                                  return (
                                    <button
                                      onClick={() => resolvedTenantUuid && stopSync(resolvedTenantUuid)}
                                      className="stop-button"
                                      title={resolvedTenantUuid ? 'Stop this workflow' : noTenantTitle}
                                      disabled={!resolvedTenantUuid}
                                    >
                                      ⏹ Stop
                                    </button>
                                  );
                                }
                                return null;
                              })()}
                            </td>
                          </tr>
                          
                          {/* Hierarchical Entity Details */}
                          {isExpanded && (
                            <tr className="entity-details-row">
                              <td colSpan="9" className="entity-details-container">
                                {isLoadingEntities ? (
                                  <div className="loading-entities">
                                    <div className="spinner-small"></div>
                                    Loading entity details...
                                  </div>
                                ) : entityData ? (
                                  <div className="hierarchical-entities">
                                    <div className="workflow-summary">
                                      <h4>📊 Workflow Summary</h4>
                                      <div className="summary-stats">
                                        <span className="stat">Total: <strong>{entityData.summary?.total_entities || 0}</strong></span>
                                        <span className="stat">Completed: <strong>{entityData.summary?.completed || 0}</strong></span>
                                        <span className="stat">Running: <strong>{entityData.summary?.running || 0}</strong></span>
                                        <span className="stat">Failed: <strong>{entityData.summary?.failed || 0}</strong></span>
                                        <span className="stat">Pending: <strong>{entityData.summary?.pending || 0}</strong></span>
                                      </div>
                                    </div>
                                    
                                    <div className="entities-grid">
                                      {entityData.entities && entityData.entities.map(entity => (
                                        <div key={entity.entity_type} className={`entity-card ${entity.status}`}>
                                          <div className="entity-header">
                                            <h5>
                                              {getSubTaskIcon(entity.status)} {entity.entity_type}
                                              <span className={`entity-status ${entity.status}`}>
                                                {entity.status === 'completed' ? 'Complete' :
                                                 entity.status === 'running' ? 'In Progress' :
                                                 entity.status === 'failed' ? 'Failed' : 'Pending'}
                                              </span>
                                            </h5>
                                            <div className="entity-timing">
                                              {entity.duration && entity.status === 'completed' ? (
                                                <span className="duration">{entity.duration}</span>
                                              ) : entity.status === 'running' ? (
                                                <span className="running-time">{entity.duration || 'Running'}</span>
                                              ) : null}
                                            </div>
                                          </div>
                                          
                                          {entity.progress && (
                                            <div className="entity-summary">
                                              <span className="records-processed">
                                                {entity.progress}
                                              </span>
                                            </div>
                                          )}

                                          {entity.error && (
                                            <div className="entity-error">
                                              <strong>Error:</strong> {entity.error}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="no-entity-data">
                                    No detailed entity information available for this workflow.
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* Show sync history (includes client_fanout, entity syncs, etc.) */}
                    {(syncHistory || []).map(entry => {
                      const tenant = tenants.find(t => t.id === entry.tenant_id);
                      const duration = entry.completed_at && entry.started_at ?
                        Math.floor((new Date(entry.completed_at) - new Date(entry.started_at)) / 1000) : null;

                      return (
                        <tr key={`sync-${entry.id}`}>
                          <td className="tenant-cell">
                            <div className="tenant-info">
                              <span className="tenant-name">
                                {tenant?.name || entry.tenant_name || entry.tenant_id}
                              </span>
                            </div>
                          </td>
                          <td className="status-cell">
                            <span className={`status-badge ${entry.status}`}>
                              {entry.status === 'completed' ? 'COMPLETED' :
                               entry.status === 'running' ? 'RUNNING' :
                               entry.status === 'failed' ? 'FAILED' : entry.status?.toUpperCase()}
                            </span>
                          </td>
                          <td className="type-cell">
                            <span className="sync-type">
                              {entry.workflow_type === 'client_fanout' ? (
                                <>
                                  📋 Client Fan-out
                                  {entry.metadata && entry.metadata.mode && (
                                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                      {entry.metadata.mode === 'all' ? `All clients (${entry.metadata.resolved_client_count || '?'})` :
                                       entry.metadata.mode === 'search' ? `Search: ${entry.metadata.explicit_client_count || entry.metadata.resolved_client_count || 0} clients` :
                                       entry.metadata.mode === 'alpha_slice' ? `Alpha: ${entry.metadata.bucket} (${entry.metadata.resolved_client_count || '?'} clients)` :
                                       entry.metadata.mode}
                                    </div>
                                  )}
                                </>
                              ) : entry.workflow_type || 'Sync'}
                            </span>
                          </td>
                          <td className="duration-cell">
                            {entry.status === 'running' || entry.status === 'pending' ? (
                              <div className="running-duration">
                                <div className="duration-label">⏱️ Running</div>
                                {entry.started_at && (
                                  <small className="started-time">
                                    Started: {new Date(entry.started_at).toLocaleTimeString()}
                                  </small>
                                )}
                              </div>
                            ) : (
                              <div className="completed-duration">
                                <div className="duration-value">
                                  {duration ? formatDuration(duration) : '-'}
                                </div>
                                {entry.completed_at && (
                                  <small className="completed-time">
                                    {new Date(entry.completed_at).toLocaleString()}
                                  </small>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="progress-cell">
                            {entry.metadata && entry.metadata.resolved_client_count && (
                              <div className="progress-info">
                                {entry.metadata.resolved_client_count} clients
                              </div>
                            )}
                          </td>
                          <td className="actions-cell">
                            {/* No expansion for sync history items */}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Configuration Modal */}
        {selectedTenant && (
          <div className="sync-config-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Sync Configuration: {selectedTenant.name}</h3>
                <button 
                  onClick={() => setSelectedTenant(null)}
                  className="close-modal"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="config-group">
                  <label>Sync Schedule</label>
                  <select 
                    defaultValue={selectedTenant.sync_schedule || 'manual'}
                    onChange={(e) => configureTenantSync(selectedTenant.id, {
                      sync_schedule: e.target.value
                    })}
                  >
                    <option value="manual">Manual Only</option>
                    <option value="hourly">Every Hour</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                
                <div className="config-group">
                  <label>Entity Types to Sync</label>
                  <div className="entity-checkboxes">
                    {['users', 'locations', 'drugs', 'services', 'appointments'].map(entity => (
                      <label key={entity}>
                        <input 
                          type="checkbox" 
                          defaultChecked={selectedTenant.sync_entities?.[entity] !== false}
                          onChange={(e) => configureTenantSync(selectedTenant.id, {
                            sync_entities: {
                              ...selectedTenant.sync_entities,
                              [entity]: e.target.checked
                            }
                          })}
                        />
                        {entity.charAt(0).toUpperCase() + entity.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="config-group">
                  <label>API Configuration</label>
                  <input 
                    type="text" 
                    placeholder="Pabau API Key"
                    defaultValue={selectedTenant.pabau_api_key ? '••••••••' : ''}
                  />
                  <input 
                    type="text" 
                    placeholder="Clinic ID"
                    defaultValue={selectedTenant.clinic_id || ''}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  onClick={() => setSelectedTenant(null)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    // Save configuration
                    setSelectedTenant(null);
                  }}
                  className="save-button"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSync;